/**
 * What the visitor agreed to have loaded, and what is loaded because of it.
 *
 * **The optional categories start off** — FB-C11. Legacy's customise panel opens with both switched on
 * and *save selection* writes whatever they hold, so a visitor who opens the panel to make a choice and
 * makes none is recorded as consenting to Google Analytics and the Meta Pixel. Both are configured and
 * live on this data. *Planet49* (C-673/17) is explicit that a pre-checked box is not consent, because
 * consent needs a *"clear affirmative action"* and doing nothing is not one.
 *
 * **The gate itself is legacy's and is kept.** Nothing is injected before a decision; GA needs
 * `analytics`; the Meta and TikTok pixels need `marketing`; the check runs again when the decision
 * changes rather than only at boot. Reject-all injects nothing at all. The defect was the defaults.
 */

export const CONSENT_KEY = 'fk-cookie-consent-v1'
/** Legacy's own event name, so anything already listening keeps working. */
export const CONSENT_EVENT = 'fk:cookie-consent'

export interface CookieConsent {
  /** Always true: the session cookie and the branch cookie are not optional and are not asked about. */
  necessary: true
  analytics: boolean
  marketing: boolean
  decidedAt: string
}

/** What the customise panel opens with. Both off - this is the whole of FB-C11. */
export const UNDECIDED = { analytics: false, marketing: false }

export function readConsent(): CookieConsent | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(CONSENT_KEY)
    if (raw === null) return null
    return consentFrom(JSON.parse(raw))
  } catch {
    return null
  }
}

/**
 * A stored value only counts if it is a recognisable decision.
 *
 * `necessary === true` and a `decidedAt` string are legacy's own checks, and they are the right ones:
 * anything else in that key is a half-write or another version's shape, and treating it as a decision
 * would silently skip asking.
 */
export function consentFrom(value: unknown): CookieConsent | null {
  if (typeof value !== 'object' || value === null) return null
  const row = value as Record<string, unknown>
  if (row.necessary !== true) return null
  if (typeof row.decidedAt !== 'string' || row.decidedAt.trim() === '') return null
  return {
    necessary: true,
    analytics: row.analytics === true,
    marketing: row.marketing === true,
    decidedAt: row.decidedAt,
  }
}

export function buildConsent(
  choice: { analytics: boolean; marketing: boolean },
  now: Date,
): CookieConsent {
  return {
    necessary: true,
    analytics: choice.analytics,
    marketing: choice.marketing,
    decidedAt: now.toISOString(),
  }
}

export function writeConsent(consent: CookieConsent): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent))
    }
  } catch {
    // A locked-down profile throws. The banner still closes; nothing gets injected, which is the safe
    // direction to fail in.
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }))
  }
}

// ─── what a decision permits ─────────────────────────────────────────────────

export interface TrackerSettings {
  googleAnalyticsId: string
  metaPixelId: string
  tiktokPixelId: string
}

export type Tracker = 'google-analytics' | 'meta-pixel' | 'tiktok-pixel'

/**
 * The trackers to load for a decision, in the order legacy loads them.
 *
 * An id that is blank means the restaurant has not configured that tracker, and a consent to marketing
 * does not conjure one. Measured: GA and Meta are set, TikTok is empty.
 */
export function permittedTrackers(
  consent: CookieConsent | null,
  settings: TrackerSettings,
): Tracker[] {
  if (consent === null) return []
  const permitted: Tracker[] = []
  if (consent.analytics && settings.googleAnalyticsId.trim() !== '') permitted.push('google-analytics')
  if (consent.marketing && settings.metaPixelId.trim() !== '') permitted.push('meta-pixel')
  if (consent.marketing && settings.tiktokPixelId.trim() !== '') permitted.push('tiktok-pixel')
  return permitted
}

export function trackerSettingsOf(payload: unknown): TrackerSettings {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const text = (key: string) => (typeof row[key] === 'string' ? row[key] : '')
  return {
    googleAnalyticsId: text('googleAnalyticsId'),
    metaPixelId: text('metaPixelId'),
    tiktokPixelId: text('tiktokPixelId'),
  }
}
